import React, { useState, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { RoleManagementProps } from './type';
import { COLORS_ADMIN_PAGE } from '../conf/constants';

const RoleManagement: React.FC<RoleManagementProps> = ({
  availableRoles,
  setAvailableRoles,
  assignedRoles,
  setAssignedRoles,
  onDragEnd,
  moveRoleToAssigned,
  moveRoleToAvailable,
}) => {
  const [selectedAvailableRoles, setSelectedAvailableRoles] = useState<number[]>([]);
  const [selectedAssignedRoles, setSelectedAssignedRoles] = useState<number[]>([]);
  const isDragging = useRef(false);

  const handleDragStart = () => {
    isDragging.current = true;
  };

  const handleDragEndLocal = (result: any) => {
    const { source, destination } = result;
    
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
      isDragging.current = false;
      return;
    }
    
    if (source.droppableId === 'availableRoles' && destination.droppableId === 'assignedRoles') {
      const roleToMove = availableRoles[source.index];
      if (roleToMove) {
        moveRoleToAssigned(roleToMove);
        if (selectedAvailableRoles.includes(roleToMove.id)) {
          setSelectedAvailableRoles(selectedAvailableRoles.filter(id => id !== roleToMove.id));
        }
      }
    }
    
    if (source.droppableId === 'assignedRoles' && destination.droppableId === 'availableRoles') {
      const roleToMove = assignedRoles[source.index];
      if (roleToMove) {
        moveRoleToAvailable(roleToMove);
        if (selectedAssignedRoles.includes(roleToMove.id)) {
          setSelectedAssignedRoles(selectedAssignedRoles.filter(id => id !== roleToMove.id));
        }
      }
    }
    isDragging.current = false;
  };

  const handleSelectAvailable = (id: number) => {
    if (selectedAvailableRoles.includes(id)) {
      setSelectedAvailableRoles(selectedAvailableRoles.filter(roleId => roleId !== id));
    } else {
      setSelectedAvailableRoles([...selectedAvailableRoles, id]);
    }
  };

  const handleSelectAssigned = (id: number) => {
    if (selectedAssignedRoles.includes(id)) {
      setSelectedAssignedRoles(selectedAssignedRoles.filter(roleId => roleId !== id));
    } else {
      setSelectedAssignedRoles([...selectedAssignedRoles, id]);
    }
  };

  const handleMoveToAssigned = () => {
    if (selectedAvailableRoles.length === 0) return;
    
    const rolesToMove = availableRoles.filter(role => selectedAvailableRoles.includes(role.id));
    moveRoleToAssigned(...rolesToMove);
    setSelectedAvailableRoles([]);
  };

  const handleMoveToAvailable = () => {
    if (selectedAssignedRoles.length === 0) return;
    
    const rolesToMove = assignedRoles.filter(role => selectedAssignedRoles.includes(role.id));
    moveRoleToAvailable(...rolesToMove);
    setSelectedAssignedRoles([]);
  };

  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={onDragEnd || handleDragEndLocal}>
      <Typography variant="h6" sx={{ color: COLORS_ADMIN_PAGE.TEXT_SECONDARY }}>Roles</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ width: '30%', p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontSize: '16px', color: COLORS_ADMIN_PAGE.TEXT_SECONDARY }}>Available Roles</Typography>
          <Droppable droppableId="availableRoles">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  height: '250px',
                  overflowY: 'auto',
                  bgcolor: COLORS_ADMIN_PAGE.WHITE,
                  paddingX: '3px',
                  paddingY: '14px',
                  border: `solid 1px ${COLORS_ADMIN_PAGE.BORDER}`,
                  borderRadius: '15px',
                }}
              >
                {availableRoles.map((role, index) => (
                  <Draggable key={role.id} draggableId={role.id.toString()} index={index}>
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onClick={() => handleSelectAvailable(role.id)}
                        sx={{
                          paddingY: 2,
                          paddingX: 3,
                          bgcolor: selectedAvailableRoles.includes(role.id) ? COLORS_ADMIN_PAGE.SELECTED : COLORS_ADMIN_PAGE.LIGHT_GRAY,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: selectedAvailableRoles.includes(role.id) ? COLORS_ADMIN_PAGE.SELECTED : COLORS_ADMIN_PAGE.BACKGROUND },
                        }}
                      >
                        <Typography>{role.name}</Typography>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </Box>

        <Box sx={{ display: 'flex', maxHeight: '200px', flexDirection: 'column', justifyContent: 'center', mx: 2 }}>
          <Button
            onClick={handleMoveToAssigned}
            disabled={selectedAvailableRoles.length === 0}
            sx={{ 
              mb: 1, 
              bgcolor: selectedAvailableRoles.length > 0 ? COLORS_ADMIN_PAGE.PRIMARY : COLORS_ADMIN_PAGE.DISABLED, 
            }}
          >
            <PlayArrow sx={{ color: COLORS_ADMIN_PAGE.WHITE }} />
          </Button>
          <Button
            onClick={handleMoveToAvailable}
            disabled={selectedAssignedRoles.length === 0}
            sx={{ 
              bgcolor: selectedAssignedRoles.length > 0 ? COLORS_ADMIN_PAGE.PRIMARY : COLORS_ADMIN_PAGE.DISABLED, 
            }}
          >
            <PlayArrow sx={{ color: COLORS_ADMIN_PAGE.WHITE, transform: 'rotate(180deg)' }} />
          </Button>
        </Box>

        <Box sx={{ width: '30%', p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontSize: '16px', color: COLORS_ADMIN_PAGE.TEXT_SECONDARY }}>Assigned Roles</Typography>
          <Droppable droppableId="assignedRoles">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  height: '250px',
                  overflowY: 'auto',
                  bgcolor: COLORS_ADMIN_PAGE.WHITE,
                  paddingX: '3px',
                  paddingY: '14px',
                  border: `solid 1px ${COLORS_ADMIN_PAGE.BORDER}`,
                  borderRadius: '15px',
                }}
              >
                {assignedRoles.map((role, index) => (
                  <Draggable key={role.id} draggableId={role.id.toString()} index={index}>
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onClick={() => handleSelectAssigned(role.id)}
                        sx={{
                          paddingY: 2,
                          paddingX: 3,
                          bgcolor: selectedAssignedRoles.includes(role.id) ? COLORS_ADMIN_PAGE.SELECTED : COLORS_ADMIN_PAGE.LIGHT_GRAY,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: selectedAssignedRoles.includes(role.id) ? COLORS_ADMIN_PAGE.SELECTED : COLORS_ADMIN_PAGE.BACKGROUND },
                        }}
                      >
                        <Typography>{role.name}</Typography>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </Box>

        <Box sx={{ width: '30%', p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontSize: '16px', color: COLORS_ADMIN_PAGE.TEXT_SECONDARY }}>Effective Rights</Typography>
          <Box
            sx={{
              height: '250px',
              overflowY: 'auto',
              paddingX: '3px',
              paddingY: '14px',
              bgcolor: COLORS_ADMIN_PAGE.WHITE,
              border: `solid 1px ${COLORS_ADMIN_PAGE.BORDER}`,
              borderRadius: '15px',
            }}
          >
            {assignedRoles.length > 0 ? (
              assignedRoles.map((role) => (
                <Box key={role.id}>
                  {role.rights.map((right, index) => (
                    <Box key={index} sx={{ paddingX: 3, paddingY: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {right.title || role.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS_ADMIN_PAGE.TEXT_SECONDARY }}>
                        {right.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))
            ) : (
              <Typography sx={{ paddingLeft: 1 }}>No rights assigned</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </DragDropContext>
  );
};

export default RoleManagement;